type Task = {
  website: string;
  in_progress: number;
  last_updated: string;
  category: string;
  owner: string;
  title: string | null;
  task_structure_shared_uid: string | null;
  to_do: number;
  in_review: number;
  snooze: number;
  priority: string;
  done: number;
};

type MergedTask = Omit<Task, "website" | "owner"> & {
  websites: string[];
  owners: string[];
  usernames: string[];
};

function deduplicateTasks(tasks: Task[], usersArray: any[]): MergedTask[] {
  const taskMap = new Map<string, MergedTask>();

  // Build user lookup map for O(1) access
  const userMap = new Map<number, string>();
  for (const u of usersArray) {
    if (u.id != null) {
      userMap.set(u.id, `${u.first_name} ${u.last_name}`);
    }
  }

  for (const task of tasks) {
    if (!task.title || !task.task_structure_shared_uid) continue;

    const key = `${task.title}|${task.task_structure_shared_uid}`;
    const ownerId = Number(task.owner);
    const username = userMap.get(ownerId) || null;

    if (!taskMap.has(key)) {
      taskMap.set(key, {
        ...task,
        websites: [task.website],
        owners: [task.owner],
        usernames: username ? [username] : [],
      });
    } else {
      const existing = taskMap.get(key)!;

      existing.to_do += task.to_do;
      existing.in_progress += task.in_progress;
      existing.in_review += task.in_review;
      existing.snooze += task.snooze;
      existing.done += task.done;

      if (!existing.websites.includes(task.website)) {
        existing.websites.push(task.website);
      }

      if (!existing.owners.includes(task.owner)) {
        existing.owners.push(task.owner);
      }

      if (username && !existing.usernames.includes(username)) {
        existing.usernames.push(username);
      }
    }
  }

  return Array.from(taskMap.values());
}

function filterAndGroupTasks(tasks: any, allowedUids: any) {
  if (!Array.isArray(tasks)) return [];
  const taskMap = new Map();

  for (const task of tasks) {
    // Skip if no title/uid
    if (!task.title || !task.task_structure_shared_uid) continue;

    // Skip if uid not in allowed list
    if (!allowedUids.includes(task.task_structure_shared_uid)) continue;

    const key = `${task.title}|${task.task_structure_shared_uid}`;

    if (!taskMap.has(key)) {
      taskMap.set(key, {
        ...task,
        websites: [task.website],
        owners: [task.owner],
      });
    } else {
      const existing = taskMap.get(key);

      // Aggregate numeric fields dynamically
      for (const [k, v] of Object.entries(task)) {
        if (typeof v === "number") {
          existing[k] = (existing[k] || 0) + v;
        }
      }

      // Collect unique websites and owners
      if (task.website && !existing.websites.includes(task.website)) {
        existing.websites.push(task.website);
      }
      if (task.owner && !existing.owners.includes(task.owner)) {
        existing.owners.push(task.owner);
      }
    }
  }

  return Array.from(taskMap.values());
}

function mapOwnersToNames(ownerIds: any, allUsers: any) {
  if (!Array.isArray(allUsers)) return [];

  // Convert single ID to array for consistent handling
  const ids = Array.isArray(ownerIds) ? ownerIds : [ownerIds];

  return ids
    .filter((id) => id !== null && id !== undefined && id !== "")
    .map((id) => {
      const user = allUsers.find((u) => String(u.id) === String(id));
      return user ? `${user.first_name} ${user.last_name}` : `Unknown (${id})`;
    });
}
// Extract missing query_output:* fields (present in data but NOT in log_data)
const getMissingQueryOutputFields = (row: any) => {
  if (!row?.data) return [];

  const allRootKeys = Object.keys(row.data).filter((k) =>
    k.startsWith("query_output:")
  );

  const allLogKeys = new Set(
    row.data.log_data?.flatMap((entry: any) =>
      Object.keys(entry).filter((k) => k.startsWith("query_output:"))
    ) || []
  );

  return allRootKeys.filter((k) => !allLogKeys.has(k));
};

export {
  deduplicateTasks,
  filterAndGroupTasks,
  mapOwnersToNames,
  getMissingQueryOutputFields,
};
